import { Match, Switch } from 'solid-js'
import { CheckIcon } from '~/components/icons/check'
import { m } from '~/paraglide/messages'

type Props = {
  current: number
  total: number
  state: 'default' | 'in-progress' | 'claim' | 'claimed' | 'loading'
  onClaim: () => void
}

export function MonthlyProgressBar(props: Props) {
  const fillPct = () => {
    if (props.total <= 0) return 0
    return Math.min(100, Math.max(0, (props.current / props.total) * 100))
  }

  return (
    <Switch>
      <Match when={props.state === 'loading'}>
        <div
          data-state="loading"
          class="relative flex w-full flex-col items-center"
        >
          <div class="h-[30px] w-full shrink-0 rounded-[8px] border-4 border-white bg-track" />
        </div>
      </Match>

      <Match when={props.state === 'claim'}>
        <button
          type="button"
          onClick={() => props.onClaim()}
          data-state="claim"
          class="tap-highlight-none relative flex h-[40px] w-full items-center justify-center overflow-hidden rounded-[8px] bg-gradient-to-r from-claim-from to-claim-to text-sm font-semibold leading-[20px] text-white shadow-claim transition-transform duration-200 active:scale-[0.98]"
        >
          <span
            aria-hidden="true"
            class="pointer-events-none absolute inset-0 rounded-[8px] shadow-claim-inner"
          />
          <span
            aria-hidden="true"
            class="pointer-events-none absolute inset-0 overflow-hidden rounded-[8px]"
          >
            <span class="absolute -bottom-[60%] -left-[40%] -top-[60%] flex h-[220%] w-[60%] animate-shimmer items-center justify-center">
              <span class="h-full w-[28%] rotate-[31deg] skew-x-[3deg] bg-gradient-to-b from-white/0 via-white/55 to-white opacity-90" />
              <span class="ml-[10%] h-full w-[12%] rotate-[31deg] skew-x-[3deg] bg-gradient-to-b from-white/0 via-white/45 to-white opacity-80" />
            </span>
          </span>
          <span class="relative">{m.monthlytask_claim()}</span>
        </button>
      </Match>

      <Match when={props.state === 'claimed'}>
        <div
          data-state="claimed"
          class="relative flex h-[40px] w-full items-center justify-center gap-[8px] overflow-hidden rounded-[8px] bg-gradient-to-r from-claim-from to-claim-to opacity-40"
        >
          <span class="text-sm font-semibold leading-[20px] text-white">
            {m.monthlytask_claimed()}
          </span>
          <span class="flex h-[18px] w-[18px] items-center justify-center rounded-full bg-white">
            <CheckIcon class="h-3 w-3 text-claim-from" />
          </span>
        </div>
      </Match>

      <Match when={props.state === 'in-progress' || props.state === 'default'}>
        <div
          data-state={props.state}
          class="relative flex w-full flex-col items-center"
        >
          <div class="h-[30px] w-full shrink-0 rounded-[8px] border-4 border-white bg-track" />
          {props.state === 'in-progress' && (
            <div
              class="absolute left-[4px] top-1/2 h-[22px] -translate-y-1/2 rounded-[4px] bg-progress-cyan shadow-[inset_0_-1.5px_0_rgba(0,0,0,0.06)] transition-[width] duration-700 ease-out"
              style={{ width: `${fillPct()}%` }}
            />
          )}
          <p class="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 whitespace-nowrap text-center text-sm font-semibold leading-[20px] text-black/30">
            {props.current}/{props.total}
          </p>
        </div>
      </Match>
    </Switch>
  )
}
